import { useEffect, useMemo, useRef } from 'react';
import { useAudio } from '../contexts/AudioContext';
import { removeResumeState } from '../lib/activityIntelligence';
import {
  audioEntityFromTrack,
  persistProgressSample,
  readAudioProgress,
  restoreAudioPosition,
} from '../lib/progressCapture';
import {
  RESUME_NOTICE_ACTION_EVENT,
  emitResumeNotice,
} from '../lib/resumeNotice';

const CAPTURE_INTERVAL_MS = 15000;

const GlobalMediaProgressCapture = () => {
  const { activeSource, audioRef, currentTrack, isPlaying, useEmbedPlayer } = useAudio();
  const restoredKeyRef = useRef(null);
  const entity = useMemo(() => audioEntityFromTrack(currentTrack), [currentTrack]);
  const entityKey = entity ? `${entity.type}:${entity.id}` : null;

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio || !entity || activeSource !== 'audio' || useEmbedPlayer) return undefined;

    const metadata = {
      source: 'global-audio-player',
      route: currentTrack?.id ? `/mixtapes/${currentTrack.id}` : '/mixtapes',
      artist: currentTrack?.artist,
      cover: currentTrack?.cover,
      source_id: currentTrack?.id || currentTrack?.track_id,
      track_type: currentTrack?.type || 'mixtape',
      hearthis_url: currentTrack?.hearthis_url || currentTrack?.externalUrl,
      resolved_audio_url: currentTrack?.resolved_audio_url,
      audio_url: currentTrack?.audio_url,
      stream_url: currentTrack?.stream_url,
      creator_id: currentTrack?.creator_id,
      creator_slug: currentTrack?.creator_slug,
      provider: currentTrack?.provider,
    };

    const capture = () => {
      const sample = readAudioProgress(audio);
      if (!sample) return;
      persistProgressSample({
        entity,
        positionSeconds: sample.position,
        durationSeconds: sample.duration,
        metadata,
      });
    };

    const restore = () => {
      if (restoredKeyRef.current === entityKey) return;
      const restored = restoreAudioPosition(audio, entity);
      if (restored > 0) {
        restoredKeyRef.current = entityKey;
        emitResumeNotice({ entity, position: restored, unit: 'seconds', kind: 'listening' });
      }
    };

    const handleResumeAction = (event) => {
      const detail = event.detail;
      if (detail?.action !== 'start-over' || detail?.notice?.id !== entityKey) return;
      try { audio.currentTime = 0; } catch { /* noop */ }
      removeResumeState(entity);
      restoredKeyRef.current = entityKey;
    };
    const handleEnded = () => capture();
    const handlePause = () => capture();
    const handlePageHide = () => capture();

    audio.addEventListener('loadedmetadata', restore);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener(RESUME_NOTICE_ACTION_EVENT, handleResumeAction);

    if (audio.readyState >= 1) restore();
    const timer = isPlaying ? window.setInterval(capture, CAPTURE_INTERVAL_MS) : null;

    return () => {
      if (timer) window.clearInterval(timer);
      audio.removeEventListener('loadedmetadata', restore);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener(RESUME_NOTICE_ACTION_EVENT, handleResumeAction);
      capture();
    };
  }, [activeSource, audioRef, currentTrack, entity, entityKey, isPlaying, useEmbedPlayer]);

  return null;
};

export { CAPTURE_INTERVAL_MS };
export default GlobalMediaProgressCapture;
